import { DEBUG, IS_KIOSK, KIOSK_CHAT_USERNAMES } from '../../../config';
import {
  clearKioskChatFailed, getKioskChatId, markKioskChatFailed, setKioskChatId,
} from '../../../util/kiosk';
import { pause } from '../../../util/schedulers';
import { callApi } from '../../../api/gramjs';
import { getGlobal } from '../../index';
import { fetchChatByUsername } from './chats';

const PRIVACY_APPLIED_KEY = 'tt-kiosk-privacy-applied';
const RESOLVE_ATTEMPTS = 15;
const RESOLVE_RETRY_DELAY_MS = 2000;

const usernamesInFlight = new Set<string>();

// Resolves every kiosk bot to its chat id (never opens a chat) and applies the kiosk privacy once per account.
// Idempotent: usernames already stored or being resolved are skipped.
export function ensureKioskChats() {
  if (!IS_KIOSK) return;

  KIOSK_CHAT_USERNAMES.forEach((username) => {
    if (getKioskChatId(username)) return;
    void resolveKioskChat(username);
  });

  void applyKioskPrivacy();
}

export function retryKioskChat(username: string) {
  clearKioskChatFailed(username);
  void resolveKioskChat(username);
}

async function resolveKioskChat(username: string) {
  if (usernamesInFlight.has(username)) return;
  usernamesInFlight.add(username);

  try {
    for (let attempt = 0; attempt < RESOLVE_ATTEMPTS; attempt++) {
      const global = getGlobal();
      if (global.auth.state === 'authorizationStateReady') {
        try {
          const chat = await fetchChatByUsername(global, username);
          if (chat) {
            setKioskChatId(username, chat.id);
            return;
          }
          // eslint-disable-next-line no-console
          console.warn('[kiosk] username not found yet', username, 'attempt', attempt, global.connectionState);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[kiosk] resolve failed', username, 'attempt', attempt, err);
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn('[kiosk] waiting for auth', username, global.auth.state, global.connectionState);
      }

      await pause(RESOLVE_RETRY_DELAY_MS);
    }

    markKioskChatFailed(username);
  } finally {
    usernamesInFlight.delete(username);
  }
}

async function applyKioskPrivacy() {
  const { currentUserId } = getGlobal();
  if (!currentUserId) return;

  try {
    if (localStorage.getItem(PRIVACY_APPLIED_KEY) === currentUserId) return;
  } catch (err) {
    return;
  }

  try {
    await callApi('updateContactSignUpNotification', true);
    await callApi('setPrivacySettings', 'chatInvite', { visibility: 'contacts', botsPrivacy: 'none' });
    await callApi('setPrivacySettings', 'phoneNumber', { visibility: 'nobody', botsPrivacy: 'none' });
    localStorage.setItem(PRIVACY_APPLIED_KEY, currentUserId);
  } catch (err) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error('Failed to apply kiosk privacy settings', err);
    }
  }
}
